import React, { useEffect, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

// v2/v3 + legacy 兼容
const PLAN_ORDER = ["FREE", "STARTER", "GROWTH", "PRO", "FREE_TRIAL", "BRONZE", "SILVER", "GOLD"];
const planRank = (code) => {
  const i = PLAN_ORDER.indexOf(code);
  return i === -1 ? 999 : i;
};

function PlanCompareTable({ plans, myPlan }) {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("PlanCompareTable", "plan/PlanCompareTable/PlanCompareTable_lang");
  }, [language]);
  const LANGUAGE = componentLanguageData.PlanCompareTable || { Main: {} };
  const L = LANGUAGE.Main || {};

  // 依 PLAN_ORDER 排序 plans
  const sortedPlans = [...plans].sort((a, b) => planRank(a.code) - planRank(b.code));

  // 方案顯示名 / 描述（v2/v3 用 Content20~27、legacy 用 Content9~13/15~19）
  const planLabel = (code) =>
    ({
      FREE: L.Content26,
      STARTER: L.Content20,
      GROWTH: L.Content21,
      PRO: L.Content22,
      FREE_TRIAL: L.Content9,
      BRONZE: L.Content10,
      SILVER: L.Content11,
      GOLD: L.Content12,
    }[code] || code);

  const dash = L.ValueDash || "—";

  // boolean row：true 顯示 ✓ 綠勾，false 顯示破折號（依設計稿）
  const checkOrDash = (b) =>
    b ? <CheckCircleIcon sx={{ color: COLORS.safe, fontSize: 18 }} /> : dash;

  const wizardExec = (n) => {
    if (n < 0) return L.ValueUnlimited || "無上限";
    if (n === 0) return L.ValueCannotExec || "無法執行";
    return (L.UnitCount || "{n} 個").replace("{n}", n);
  };

  // 表格分組（rows × plans 矩陣）—— 不含「價格」section（價格在升級卡片已有）
  // 順序：策略精靈 → AI 策略助理 → MasQuant SDK（依 screenshot 4 desired）
  const SECTIONS = [
    {
      title: L.GroupWizard || "策略精靈",
      rows: [
        { label: L.RowWizardGen, render: (p) => (L.UnitTimes || "{n} 次").replace("{n}", p.wizardMonthlyLimit ?? 0) },
        { label: L.RowWizardSave, render: (p) => (L.UnitCount || "{n} 個").replace("{n}", p.wizardSaveLimit ?? 0) },
        { label: L.RowWizardExec, render: (p) => wizardExec(p.wizardExecLimit ?? 0) },
      ],
    },
    {
      title: L.GroupAi || "AI 策略助理",
      rows: [
        { label: L.RowAiGen, render: (p) => (L.UnitPerDay || "{n} 次 / 日").replace("{n}", p.strategyDailyLimit ?? 0) },
        { label: L.RowAiAnalyze, render: (p) => (L.UnitPerDay || "{n} 次 / 日").replace("{n}", p.backtestDailyLimit ?? 0) },
        { label: L.RowCoinGen, render: (p) => (L.UnitPerDay || "{n} 次 / 日").replace("{n}", p.coinStrategyDailyLimit ?? 0) },
        { label: L.RowCoinAnalyze, render: (p) => (L.UnitPerDay || "{n} 次 / 日").replace("{n}", p.coinBacktestDailyLimit ?? 0) },
        { label: L.RowSaveLimit, render: (p) => (L.UnitCount || "{n} 個").replace("{n}", p.strategySaveLimit ?? 0) },
        { label: L.RowConcurrentLimit, render: () => L.ValueUnlimited || "無上限" },
      ],
    },
    {
      title: L.GroupMaslib || "MasQuant SDK 數據中心",
      rows: [
        { label: L.RowMaslibBasic, render: (p) => checkOrDash(p.maslibBasicAccess) },
        { label: L.RowMaslibFull, render: (p) => checkOrDash(p.maslibFullAccess) },
        { label: L.RowMaslibKpi, render: (p) => checkOrDash(p.maslibKpiAccess) },
        { label: L.RowMaslibVisual, render: (p) => checkOrDash(p.maslibVisualAccess) },
      ],
    },
  ];

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{ p: 0, boxShadow: "none", backgroundColor: "transparent", borderRadius: 0, overflowX: "auto" }}
      >
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          {/* === Plan header（columns）：純文字、主推方案（PRO）欄字色橘 === */}
          <TableBody>
            <TableRow sx={{ borderBottom: "1px solid #eee" }}>
              <TableCell
                sx={{
                  width: "26%",
                  py: 2,
                  ...TYPO.title,
                  color: COLORS.black,
                }}
              >
                {L.Content2 || "項目"}
              </TableCell>
              {sortedPlans.map((p) => {
                // 主推方案（PRO / legacy GOLD）用橘色強調，引導升級
                const isPopular = ["PRO", "GOLD"].includes(p.code);
                return (
                  <TableCell
                    key={p.code}
                    align="left"
                    sx={{
                      py: 2,
                      ...TYPO.bodyM,
                      fontWeight: 600,
                      color: isPopular ? COLORS.main : COLORS.grayDark,
                    }}
                  >
                    {planLabel(p.code)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>

          {/* === 分組 sections === */}
          {SECTIONS.map((section) => (
            <TableBody key={section.title}>
              {/* 分組標題列：淡灰底 */}
              <TableRow sx={{ backgroundColor: COLORS.grayLightest }}>
                <TableCell
                  colSpan={sortedPlans.length + 1}
                  sx={{ py: 1, ...TYPO.bodyM, fontWeight: 600, color: COLORS.black }}
                >
                  {section.title}
                </TableCell>
              </TableRow>
              {/* 功能 rows */}
              {section.rows.map((row, idx) => (
                <TableRow
                  key={row.label || idx}
                  sx={{ "&:hover": { backgroundColor: COLORS.grayLightest } }}
                >
                  <TableCell sx={{ py: 1.5, ...TYPO.bodyM, color: COLORS.black }}>
                    {row.label}
                  </TableCell>
                  {sortedPlans.map((p) => {
                    // 主推方案（PRO / legacy GOLD）用橘色強調，引導升級
                    const isPopular = ["PRO", "GOLD"].includes(p.code);
                    return (
                      <TableCell
                        key={p.code}
                        align="left"
                        sx={{
                          py: 1.5,
                          ...TYPO.bodyM,
                          color: isPopular ? COLORS.main : COLORS.black,
                          fontWeight: isPopular ? 600 : 400,
                        }}
                      >
                        {row.render(p)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          ))}
        </Table>
      </TableContainer>
    </Box>
  );
}

export default PlanCompareTable;
